import { useState, useCallback, useEffect, Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import type { AnalysisResult, DiagramScene, Stratum } from '../api/types'
import type { ParsedStatement } from '../utils/types'
import { NodeWorkspace } from './NodeWorkspace'
import { StratumNavigator } from './StratumNavigator'
import type { StratumChangeInfo } from './StratumNavigator'
import styles from './Workspace.module.css'

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
    state = { error: null as Error | null }
    static getDerivedStateFromError(error: Error) { return { error } }
    componentDidCatch(error: Error, info: ErrorInfo) { console.error('[StratumCrash]', error, info) }
    render() {
        if (this.state.error) {
            return (
                <div 
                    className="m-4 p-4 rounded-xl border border-danger/20 font-mono text-xs"
                    style={{ background: 'color-mix(in srgb, var(--danger) 5%, transparent)' }}
                >
                    <div className="text-danger font-black uppercase tracking-widest mb-2 flex items-center gap-2">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        Narrative Error
                    </div>
                    <div className="text-text-secondary leading-relaxed mb-4">{this.state.error.message}</div>
                    <div className="opacity-40 text-2xs overflow-hidden whitespace-pre">
                        {this.state.error.stack?.split('\n').slice(0, 4).join('\n')}
                    </div>
                </div>
            )
        }
        return this.props.children
    }
}

interface WorkspaceProps {
    result?: AnalysisResult | null
    scene?: DiagramScene | null
    parsedStatements?: ParsedStatement[]
    focusedNodeId?: string | null
    focusedStatement?: ParsedStatement | null
    onNodeSelect?: (nodeId: string) => void
    onStratumChange?: (info: StratumChangeInfo) => void
    focusStack?: string[]
    onPushFocus?: (id: string) => void
}

export function Workspace({
    result, scene, parsedStatements: _parsedStatements,
    focusedNodeId, focusedStatement,
    onNodeSelect, onStratumChange,
    focusStack: externalFocusStack, onPushFocus,
}: WorkspaceProps) {
    // Local state if not provided externally
    const [internalStack, setInternalStack] = useState<string[]>([])
    const focusStack = externalFocusStack || internalStack

    const handlePushFocus = useCallback((id: string) => {
        if (onPushFocus) onPushFocus(id)
        else setInternalStack(prev => [...prev, id])
    }, [onPushFocus])

    const handleStratumChange = useCallback((info: StratumChangeInfo) => {
        onStratumChange?.(info)
    }, [onStratumChange])

    const hierarchy = result?.stratumHierarchy

    // Sync stratum change when stack pops via external control
    useEffect(() => {
        if (!hierarchy) return
        const focusedId = focusStack.length > 0 ? focusStack[focusStack.length - 1] : null
        const root = hierarchy.strata[hierarchy.rootId]
        if (focusedId) {
            const stratum = hierarchy.strata[focusedId]
            if (stratum) onStratumChange?.(stratumChangeInfo(stratum))
        } else {
            onStratumChange?.(stratumChangeInfo(null, root))
        }
    }, [focusStack, hierarchy])

    const hasStratum = !!hierarchy

    // Fallback: no stratum hierarchy
    if (!hasStratum) {
        if (focusedNodeId && scene) {
            return (
                <div className={styles.panel}>
                    <NodeWorkspace
                        scene={scene}
                        nodeId={focusedNodeId}
                        statement={focusedStatement ?? null}
                    />
                </div>
            )
        }

        return <div className={styles.panel} />
    }

    // ── Unified stratum view (no tabs) ──
    return (
        <div className={styles.panel}>
            <ErrorBoundary><StratumNavigator
                hierarchy={hierarchy!}
                scene={scene ?? null}
                onStratumChange={handleStratumChange}
                onStepFocus={onNodeSelect}
                focusStack={focusStack}
                onPushFocus={handlePushFocus}
            /></ErrorBoundary>
        </div>
    )
}

function buildStructuralKey(stratum: Stratum): string {
    const depthPart = stratum.depth != null ? `${stratum.depth}` : '?'
    const role = stratum.structuralRole ?? 'NONE'
    return `${stratum.kind}:${depthPart}:${role}`
}

function stratumChangeInfo(stratum: Stratum | null, root?: Stratum | null, isAutoInlined?: boolean): StratumChangeInfo {
    if (stratum) {
        return {
            stratumId: stratum.id,
            structuralKey: buildStructuralKey(stratum),
            nodeIds: [...(stratum.nodeIds ?? [])],
            name: stratum.name || stratum.id,
            isAutoInlined,
        }
    }
    return {
        stratumId: null,
        structuralKey: null,
        nodeIds: [...(root?.nodeIds ?? [])],
        name: root?.name ?? 'System',
    }
}
